<template>
  <div class="org-node" :class="{ 'org-node--branch': node.children.length }">
    <div class="org-node__card-wrap">
      <div
        role="button"
        tabindex="0"
        class="demo-card"
        :class="{
          'is-active': pendingEmail === node.persona.email,
          'is-executive': !node.managerKey,
          'is-manager-card': node.children.length > 0
        }"
        @click="$emit('select', node.persona)"
        @keydown.enter.prevent="$emit('select', node.persona)"
        @keydown.space.prevent="$emit('select', node.persona)"
      >
        <div class="demo-card__top">
          <div>
            <h3 class="demo-card__name">{{ fullName(node.persona) }}</h3>
            <p class="demo-card__title">{{ node.persona.title || 'Team Member' }}</p>
          </div>
          <div class="demo-card__controls">
            <button
              v-if="node.children.length"
              type="button"
              class="demo-card__toggle"
              :aria-label="expanded ? 'Collapse reports' : 'Expand reports'"
              @click.stop="toggleExpanded"
            >
              {{ expanded ? '-' : '+' }}
            </button>
            <button
              v-if="showPersonaDetails && detailRows.length"
              type="button"
              class="demo-card__toggle demo-card__toggle--detail"
              :aria-label="detailsExpanded ? 'Hide details' : 'Show details'"
              @click.stop="toggleDetails"
            >
              <i class="mdi" :class="detailsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
            </button>
            <i class="mdi mdi-arrow-right"></i>
          </div>
        </div>

        <div class="demo-card__emails">
          <span
            v-for="emailAddr in allEmails"
            :key="emailAddr"
            class="demo-card__email"
          >
            <i class="mdi mdi-email-outline demo-card__email-icon"></i>{{ emailAddr }}
          </span>
        </div>

        <div class="demo-card__badges">
          <span v-if="node.persona.mergedEmails && node.persona.mergedEmails.length > 1" class="demo-badge is-merged">
            <i class="mdi mdi-link-variant"></i> Merged
          </span>
          <span v-if="node.persona.isAdmin" class="demo-badge">Admin</span>
          <span v-if="node.persona.isManager" class="demo-badge is-manager">Manager</span>
          <span v-if="node.children.length" class="demo-badge is-report-count">
            {{ node.children.length }} direct reports
          </span>
          <button
            type="button"
            class="demo-badge demo-badge--toggle"
            :class="node.persona.isAccountActivated ? 'is-activated' : 'is-pending'"
            @click.stop="onToggleActivation"
          >
            <i class="mdi" :class="node.persona.isAccountActivated ? 'mdi-check-circle-outline' : 'mdi-clock-outline'"></i>
            {{ node.persona.isAccountActivated ? 'Activated' : 'Pending Activation' }}
          </button>
        </div>

        <div
          v-if="showPersonaDetails && detailRows.length && detailsExpanded"
          class="demo-card__details"
          @click.stop
        >
          <div
            v-for="detail in detailRows"
            :key="detail.label"
            class="demo-card__detail-row"
          >
            <span class="demo-card__detail-label">{{ detail.label }}</span>
            <span class="demo-card__detail-value">{{ detail.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="node.children.length && expanded"
      class="org-node__children"
      :style="{ '--child-count': node.children.length }"
    >
      <div class="org-node__trunk"></div>
      <div class="org-node__rail"></div>
      <div class="org-node__children-grid" :style="{ '--child-count': node.children.length }">
        <OrgTreeNode
          v-for="child in node.children"
          :key="child.key"
          :node="child"
          :pending-email="pendingEmail"
          :submitting="submitting"
          :show-persona-details="showPersonaDetails"
          @select="$emit('select', $event)"
          @toggle-activation="$emit('toggle-activation', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrgTreeNode',
  props: {
    node: {
      type: Object,
      required: true,
    },
    pendingEmail: {
      type: String,
      default: '',
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    showPersonaDetails: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select', 'toggle-activation'],
  data() {
    return {
      expanded: true,
      detailsExpanded: false,
    }
  },
  computed: {
    allEmails() {
      const merged = this.node.persona.mergedEmails;
      if (Array.isArray(merged) && merged.length > 1) return merged;
      return this.node.persona.email ? [this.node.persona.email] : [];
    },

    detailRows() {
      const skillList = this.topSkillList(this.node.persona)
      const summary = this.summaryText(this.node.persona)
      const organization = String(this.node.persona?.organization?.name || '').trim()

      return [
        { label: 'Organization', value: organization },
        { label: 'Top skills', value: skillList },
        { label: 'Summary', value: summary },
      ].filter((detail) => detail.value)
    },
  },
  methods: {
    fullName(persona) {
      return [persona.firstName, persona.lastName].filter(Boolean).join(' ')
    },
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    toggleDetails() {
      this.detailsExpanded = !this.detailsExpanded
    },
    onToggleActivation() {
      this.$emit('toggle-activation', {
        userId: this.node.persona._id,
        isAccountActivated: !this.node.persona.isAccountActivated,
      })
    },
    topSkillList(persona) {
      const skills = Array.isArray(persona?.resumeAnalysis?.skills) ? persona.resumeAnalysis.skills : []
      const values = skills
        .slice(0, 4)
        .map((skill) => skill?.skill || skill?.name || skill)
        .filter(Boolean)

      return values.join(', ')
    },
    summaryText(persona) {
      const summary = String(persona?.resumeAnalysis?.summary || '').replace(/\s+/g, ' ').trim()
      if (!summary) return ''
      if (summary.length <= 140) return summary
      return `${summary.slice(0, 140).trim()}...`
    },
  },
}
</script>

<style lang="scss" scoped>
.org-node {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.9rem;
}

.org-node--branch > .org-node__card-wrap {
  position: relative;
}

.org-node__card-wrap {
  position: relative;
  width: 100%;
}

.org-node__children {
  position: relative;
  margin-left: 1.4rem;
  padding-left: 1.25rem;
  overflow: hidden;
}

.org-node__trunk {
  position: absolute;
  top: 0;
  bottom: 0.35rem;
  left: 0;
  width: 2px;
  background: linear-gradient(180deg, rgba(71, 84, 103, 0.45) 0%, rgba(71, 84, 103, 0.18) 100%);
}

.org-node__rail {
  display: none;
}

.org-node__children-grid {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.org-node__children-grid > .org-node > .org-node__card-wrap::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -1.25rem;
  width: 1.25rem;
  height: 2px;
  background: rgba(71, 84, 103, 0.4);
  transform: translateY(-50%);
}

.org-node__children-grid > .org-node:last-child > .org-node__card-wrap::after {
  content: '';
  position: absolute;
  top: 50%;
  left: -1.25rem;
  width: 2px;
  height: 9999px;
  background: $color-surface;
  transform: translateY(0);
}

.demo-card {
  width: min(100%, 420px);
  padding: 0.82rem 0.86rem;
  border: 1px solid $color-border;
  border-radius: 16px;
  background: linear-gradient(180deg, $color-surface 0%, $color-highlight-strong 100%);
  box-shadow: $shadow-xs;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  text-align: left;
  cursor: pointer;
}

.demo-card:hover,
.demo-card:focus,
.demo-card:focus-visible {
  transform: translateY(-1px);
  box-shadow: $shadow-md;
  border-color: rgba(170, 118, 214, 0.45);
  outline: none;
}

.demo-card.is-active {
  border-color: $color-primary;
  box-shadow: 0 10px 22px rgba(170, 118, 214, 0.14);
}

.demo-card.is-executive {
  background: linear-gradient(180deg, #fff 0%, $color-primary-soft 100%);
  border-color: rgba(170, 118, 214, 0.34);
}

.demo-card.is-manager-card {
  box-shadow: 0 10px 24px rgba(30, 80, 162, 0.08);
}

.demo-card:disabled {
  cursor: wait;
  opacity: 0.72;
}

.demo-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.55rem;
}

.demo-card__controls {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.demo-card__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  border: 1px solid $color-border-strong;
  border-radius: 999px;
  background: $color-surface;
  color: $color-primary-dark;
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}

.demo-card__toggle:hover,
.demo-card__toggle:focus {
  border-color: $color-primary;
  background: $color-primary-soft;
}

.demo-card__top i {
  font-size: 0.95rem;
  color: $color-primary-dark;
}

.demo-card__toggle--detail i {
  font-size: 0.9rem;
}

.demo-card__name {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 800;
  color: $color-text;
  line-height: 1.2;
}

.demo-card__title {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  line-height: 1.25;
  color: $color-text-muted;
}

.demo-card__emails {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  margin-top: 0.55rem;
}

.demo-card__email {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.76rem;
  line-height: 1.25;
  color: $color-text-muted;
  word-break: break-word;
}

.demo-card__email-icon {
  font-size: 0.8rem;
  flex-shrink: 0;
  color: $color-text-subtle;
}

.demo-badge.is-merged {
  background: $color-info-soft;
  color: $color-primary-deep;
  gap: 0.25rem;

  i { font-size: 0.75rem; }
}

.demo-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.7rem;
}

.demo-card__details {
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.demo-card__detail-row {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}

.demo-card__detail-label {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $color-text-subtle;
}

.demo-card__detail-value {
  font-size: 0.8rem;
  line-height: 1.4;
  color: $color-text;
}

.demo-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: $radius-pill;
  background: $color-primary-soft;
  color: $color-primary-dark;
  font-size: 0.7rem;
  font-weight: 700;
}

.demo-badge.is-manager {
  background: $color-info-soft;
  color: $color-primary-deep;
}

.demo-badge.is-report-count {
  background: $color-surface-muted;
  color: $color-text-muted;
}

.demo-badge--toggle {
  border: none;
  cursor: pointer;
  font-family: inherit;
  gap: 0.3rem;

  i { font-size: 0.85rem; }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
}

.demo-badge--toggle.is-activated {
  background: $color-success-soft;
  color: $color-success-strong;
}

.demo-badge--toggle.is-pending {
  background: rgba($color-danger, 0.12);
  color: $color-danger-strong;
}

@media (max-width: 768px) {
  .org-node__children {
    margin-left: 0.8rem;
    padding-left: 0.9rem;
  }

  .demo-card {
    width: 100%;
  }
}
</style>
